'use client';

/**
 * Demo quick-login — one-click sign-in on the login page (demo mode only).
 *
 * Avoids needing a password: posts the chosen demo user to /api/v1/demo/login
 * (which sets the sig_demo_user cookie) and redirects. Rendered only when
 * NEXT_PUBLIC_DEMO_MODE is on, so it never appears in production.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DEMO_SIMPLE } from '@/lib/demo-simple';
import { cn } from '@/lib/utils';

const USERS = [
  { email: 'demo+premium@stocksinglobal.com.au', label: 'Premium' },
  { email: 'demo+pro@stocksinglobal.com.au', label: 'Pro' },
  { email: 'demo+basic@stocksinglobal.com.au', label: 'Basic' },
] as const;

export function DemoQuickLogin({ next }: { next?: string }) {
  const [loading, setLoading] = useState<string | null>(null);

  // Simple-demo mode shows ONLY the Premium subscriber button. The array stays
  // intact (Pro/Basic remain in the RBAC allowlists); we just filter the render.
  const users = DEMO_SIMPLE
    ? USERS.filter((u) => u.email === 'demo+premium@stocksinglobal.com.au')
    : USERS;

  async function go(email: string) {
    if (loading) return; // a second click would race two cookie writes
    setLoading(email);
    try {
      await fetch('/api/v1/demo/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      /* ignore — redirect anyway; the cookie attempt is best-effort */
    }
    window.location.href = next || '/stocks/CBA';
  }

  return (
    <div className="mt-5 rounded-[4px] border border-dashed border-[var(--color-hair-2)] p-3">
      <p className="mb-2 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-faint)]">
        Demo — one-click login, no password
      </p>
      <div className={cn('grid gap-2', users.length === 1 ? 'grid-cols-1' : 'grid-cols-3')}>
        {users.map((u) => (
          <Button
            key={u.email}
            type="button"
            variant="secondary"
            size="sm"
            disabled={loading !== null}
            aria-busy={loading === u.email}
            onClick={() => go(u.email)}
          >
            {loading === u.email ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {u.label}
              </>
            ) : (
              u.label
            )}
          </Button>
        ))}
      </div>

      {/* Matches DevQuickLogin: a bare spinner says something is happening,
          this says the right thing is. */}
      <p
        role="status"
        aria-live="polite"
        className="mt-2 h-4 text-center font-mono text-[10px] tracking-[0.08em] text-[var(--color-faint)]"
      >
        {loading ? `Signing in as ${users.find((u) => u.email === loading)?.label ?? ''}…` : ''}
      </p>
    </div>
  );
}
